import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { jt, t } from "ttag";

import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackNamePageViewed } from "@/features/analytics/trackers/name-page-viewed";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { CATEGORY_SLUG } from "@/shared/domains";
import { useStateOptions } from "@/shared/hooks";
import { FunnelSelect, FunnelStep } from "../components";
import { FUNNEL_ROUTE } from "../router/constants";
import { useFunnelStore } from "../stores";

const inputClassName =
  "w-full rounded-xl border border-neutral-300 bg-white px-4 py-3.5 text-base text-neutral-900 outline-none placeholder:text-neutral-400 hover:border-neutral-500 focus:border-neutral-500 focus:ring-2 focus:ring-secondary-200 focus:ring-offset-1";

// Mirrors the projected-timeline math: 15% of body weight lost per month.
const MONTHLY_LOSS_RATE = 0.15;

export const MedicalReview = () => {
  const navigate = useNavigate();
  const { analytics } = useCustomerIoContext();
  const set = useFunnelStore((store) => store.set);
  const setPersonalDetails = useOnboardingStore(
    (store) => store.setPersonalDetails,
  );
  const { heightFeet, heightInches, initialWeight, expectedWeight } =
    useFunnelStore.getState();
  const { data: stateOptions } = useStateOptions(CATEGORY_SLUG.WEIGHT_LOSS);

  const [firstName, setFirstName] = useState(
    useFunnelStore.getState().firstName ?? "",
  );
  const [lastName, setLastName] = useState(
    useFunnelStore.getState().lastName ?? "",
  );
  const [state, setState] = useState(useFunnelStore.getState().state ?? "");

  useEffect(() => {
    trackNamePageViewed({
      customerIoAnalytics: analytics,
      eventPrefix: EVENT_PREFIX.FUNNEL,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    heightFeet === undefined ||
    heightInches === undefined ||
    initialWeight === undefined ||
    expectedWeight === undefined
  ) {
    return <Navigate to={FUNNEL_ROUTE.MEASUREMENTS} replace />;
  }

  const heightInInches = heightFeet * 12 + heightInches;
  const bmi = ((initialWeight * 703) / heightInInches ** 2).toFixed(1);
  const monthlyLoss = initialWeight * MONTHLY_LOSS_RATE;
  const monthsToGoal = (
    monthlyLoss > 0
      ? Math.max((initialWeight - expectedWeight) / monthlyLoss, 0)
      : 0
  ).toFixed(1);

  const isComplete =
    firstName.trim() !== "" && lastName.trim() !== "" && state !== "";

  const handleSubmit = () => {
    if (!isComplete) {
      return;
    }
    set({ firstName, lastName, state });
    setPersonalDetails({ firstName, lastName });
    navigate(FUNNEL_ROUTE.CONTACT);
  };

  const strong = <strong key="strong">{t`strong candidate`}</strong>;
  const chance = <strong key="chance">{t`94% chance`}</strong>;

  return (
    <FunnelStep
      title={t`Your Medical Review`}
      description={t`Let's proceed to check your eligibility.`}
      footerNote={t`Medication can only be shipped to certain states. Your provider review begins after you complete this form.`}
      nextDisabled={!isComplete}
      onSubmit={handleSubmit}
    >
      <dl className="space-y-2 text-lg text-primary-950">
        <div>
          <span className="font-bold">{t`BMI:`}</span> {bmi}
        </div>
        <div>
          <span className="font-bold">{t`Current Weight:`}</span>{" "}
          {t`${initialWeight}lbs`}
        </div>
        <div>
          <span className="font-bold">{t`Goal Weight:`}</span>{" "}
          {t`${expectedWeight}lbs within ${monthsToGoal} months`}
        </div>
      </dl>

      <p className="mt-4 text-lg text-primary-950">
        {jt`You are a ${strong} for medical weight loss with a ${chance} of successful treatment if qualified.`}
      </p>

      <hr className="my-8 border-secondary-200" />

      <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`First Name`}
          </span>
          <input
            className={`${inputClassName} mf-masked mf-listen`}
            placeholder={t`Enter your first name`}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`Last Name`}
          </span>
          <input
            className={`${inputClassName} mf-masked mf-listen`}
            placeholder={t`Enter your last name`}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </label>
      </div>

      <div className="mt-6">
        <span className="mb-2 block text-sm font-medium text-primary-950">
          {t`What state will your medication be shipped to?`}
        </span>
        <FunnelSelect
          value={state}
          onChange={setState}
          options={stateOptions ?? []}
        />
      </div>
    </FunnelStep>
  );
};
